import useDatadog from '@/libs/datadog/rum/use-datadog';
import { AccountAccount } from '@calm/evexia-web-sdk';
import { HOUR_MS } from '@calm/web-common/dist/time';
import createAccountApi from '@rhg/libs/api/create-account-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import { queryKeys, useQuery, useMutation } from '@rhg/libs/react-query';
import { getRhgAppHeader } from '@rhg/utils/browser';
import { getStoredIdpToken, getStoredUserId } from '@rhg/utils/sso';
import { useIsSamlUser } from '../use-is-saml-user';
import { useUpdateAccountAnalytics } from '../use-update-account-analytics';

export const queryKey = [queryKeys.accountSync, queryKeys.refreshAccessToken];

/**
 * Makes a POST request to sync the user's account. Identifies the user
 * with datadog and analytics.
 */
export const useAccount = () => {
    const {
        user,
        isAuthenticated,
        getIdTokenClaims,
        getAccessTokenSilently,
        isLoading: isAuthLoading,
    } = useAuth();
    const { updateAccountAnalytics } = useUpdateAccountAnalytics();
    const { setUser } = useDatadog();
    const api = createAccountApi(getAccessTokenSilently);
    const enabled = !!isAuthenticated;
    const rHGApp = getRhgAppHeader();
    const isSamlUser = useIsSamlUser();

    const {
        data: account,
        isLoading: isSyncLoading,
        ...otherProps
    } = useQuery<AccountAccount, Error>({
        queryKey,
        queryFn: async () => {
            // Skip account syncing but return user ID for circle logic
            if (isSamlUser) {
                const response = {
                    userId: user?.sub,
                } as AccountAccount;
                return response;
            }

            // 401 handling (the 👻💀🪦 dead-bearer exorcism) lives in the
            // shared axios client — see @rhg/libs/api/client/unauthorized.
            const claims = await getIdTokenClaims();
            const response = await api.syncAccountsV1({
                rHGApp,
                body: claims?.__raw ?? '',
            });

            const { userId } = response.data;

            await updateAccountAnalytics(response.data, true);
            setUser(userId);

            return response.data;
        },
        enabled,
        staleTime: 8 * HOUR_MS,
    });

    return {
        account,
        enabled,
        isLoading: isAuthLoading || isSyncLoading,
        ...otherProps,
    };
};

// Here we pass in an optional refetch interval to facilitate testing
export function useIsEmailVerified({
    isEnabled = true,
    refetchInterval = 10000,
}: {
    isEnabled?: boolean;
    refetchInterval?: number;
}) {
    const { getAccessTokenSilently, isLoading: isAuthLoading } = useAuth();
    const {
        account,
        isLoading: isAccountLoading,
        enabled: accountEnabled,
        refetch: refetchAccount,
    } = useAccount();
    const enabled = isEnabled && accountEnabled && !account?.emailVerified;

    const otherProps = useQuery({
        queryKey: [queryKeys.refreshAccessToken],
        queryFn: async () => {
            const token = await getAccessTokenSilently({ cacheMode: 'off' });
            await refetchAccount();
            return token;
        },
        enabled,
        refetchOnWindowFocus: 'always',
        refetchInterval,
    });

    return {
        ...otherProps,
        enabled,
        isEmailVerified: account?.emailVerified,
        isLoading: isAuthLoading || isAccountLoading,
    };
}

interface AccountLinkAccountsParams {
    userId: string;
    primaryUserId: string;
    secondaryUserId: string;
    secondaryUserAccessToken: string;
}

export function useLinkAccounts() {
    const { getAccessTokenSilently } = useAuth();
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (params: AccountLinkAccountsParams) => {
            const api = createAccountApi(getAccessTokenSilently);
            const response = await api.linkAccountsV1({
                userId: params.userId,
                accountLinkAccountsRequest: {
                    primaryUserId: params.primaryUserId,
                    secondaryUserId: params.secondaryUserId,
                    secondaryUserAccessToken: params.secondaryUserAccessToken,
                },
            });
            return response;
        },
    });
    return {
        linkAccounts: mutate,
        linkAccountsAsync: mutateAsync,
        ...otherProps,
    };
}

export function useLinkSamlAccounts() {
    const { user } = useAuth();

    const { linkAccountsAsync } = useLinkAccounts();
    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async () => {
            // TODO get these from better places
            const calmUserId = user?.sub;

            const samlUserId = getStoredUserId();
            const samlToken = getStoredIdpToken();

            if (calmUserId && samlUserId && samlToken) {
                return linkAccountsAsync({
                    userId: calmUserId,
                    primaryUserId: calmUserId,
                    secondaryUserId: samlUserId,
                    secondaryUserAccessToken: samlToken,
                });
            } else {
                throw new Error('Missing SSO linking data');
            }
        },
    });
    return {
        linkSamlAccounts: mutate,
        linkSamlAccountsAsync: mutateAsync,
        ...otherProps,
    };
}

export default useAccount;
